import { dismissDocViewer, useDocViewer } from '../../../lib/doc-viewer-presenter';
import { useBesideChatWaiting, useChatFilePane } from '../../../lib/chat-file-pane';
import { DocViewerModal } from './DocViewerModal';

/**
 * The ONE place the document viewer is rendered — the web equivalent of native hosting
 * `DocumentViewer` once in `RootView.swift`'s overlay chain / `AppRoot.kt`'s ZStack.
 *
 * Mounted by `SymphonyApp`, OUTSIDE its framed/chrome-free branch, so it sits above every page and
 * outlives any row, panel or side panel that opens it (see `lib/doc-viewer-presenter.ts` for the defect
 * that motivates it). No call site renders `DocViewerModal` itself; they all call `openDocViewer(...)`.
 *
 * `key={id}` so a second open of the same file starts a fresh modal (query + scroll state reset) rather
 * than reusing the instance the user just closed.
 *
 * A chat-opened file docked beside the conversation is drawn by `FloatingChat` instead (its `DocViewerPane`),
 * so this host draws nothing for it — `useChatFilePane` is the one place that decides which of the two owns
 * a request. The same request falls back to this modal whenever the chat cannot host it, and is drawn nowhere
 * while it waits for a chat that can (`useBesideChatWaiting`).
 */
export function DocViewerHost() {
  const request = useDocViewer();
  const docked = useChatFilePane();
  const waiting = useBesideChatWaiting();
  if (!request || docked || waiting) return null;
  return (
    <DocViewerModal
      key={request.id}
      file={request.file}
      kind={request.kind}
      pageName={request.pageName}
      fileHref={request.fileHref}
      workspaceName={request.workspaceName}
      inFileTree={request.inFileTree}
      startEditing={request.editing}
      directory={request.directory}
      draft={request.draft}
      vfsPath={request.vfsPath}
      requestId={request.id}
      onClose={dismissDocViewer}
    />
  );
}
